import { useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  DataTable,
  DataTableColumnHeader,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InputField,
  Label,
  Progress,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tag,
  Textarea,
  Upload,
  cn,
} from "@jem-hr/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeftRight,
  ArrowRight,
  Bot,
  Calendar,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleHelp,
  FileBarChart,
  FileSpreadsheet,
  FileText,
  History,
  Info,
  MapPin,
  MessageSquare,
  Pencil,
  RotateCcw,
  Send,
  ShieldAlert,
  Sparkles,
  Upload as UploadIcon,
  UserMinus,
  UserPlus,
  Users as UsersIcon,
  X,
} from "lucide-react";

/* ====================================================================== */
/*  ENG-2965 — Custom Reports tab — Roster Agent UI                       */
/*                                                                        */
/*  Operator-facing front-end for the AI Roster Ingestion Engine          */
/*  (ENG-2872, backend Done). This is a prototype: every backend call is  */
/*  mocked. The renderer walks a phased agent JSON envelope and surfaces  */
/*  typed blocks ('message', 'stat', 'summary_table', 'clarifying_        */
/*  question', 'anomaly', 'permission_request', 'error') to a jem-ui      */
/*  primitive. LangGraph tool-call interrupts are handled as a separate   */
/*  approval Dialog stream.                                               */
/*                                                                        */
/*  Surfaces:                                                             */
/*    1. Pre-import   — upload zone, resume card, agent-down state        */
/*    2. HITL review  — typed-block renderer + answer collector           */
/*    3. Tool-call    — Approve / Edit (dates only) / Reject Dialog       */
/*    4. Post-import  — assignments-live, unmatched, diff vs previous     */
/* ====================================================================== */

/* ---------------- Phase + Block types (mirror agent contract) ---------- */

type Phase =
  | "conversation"
  | "context_gathering"
  | "initial_scan"
  | "requesting_context"
  | "matching"
  | "verification"
  | "saving"
  | "extraction_complete"
  | "error";

type AnomalySeverity = "info" | "warning" | "error";

type QuestionInputType = "free_text" | "single_select" | "multi_select" | "confirm";

interface MessageBlock {
  kind: "message";
  body: string;
}

interface StatBlock {
  kind: "stat";
  label: string;
  value: string | number;
  hint?: string;
  icon?: "users" | "map" | "calendar" | "file";
}

interface SummaryTableBlock {
  kind: "summary_table";
  title: string;
  caption?: string;
  columns: { key: string; label: string }[];
  rows: Record<string, string>[];
  footnote?: string;
}

interface QuestionOption {
  value: string;
  label: string;
  sublabel?: string;
}

interface ClarifyingQuestionBlock {
  kind: "clarifying_question";
  question_id: string;
  prompt: string;
  input_type: QuestionInputType;
  options?: QuestionOption[];
  required?: boolean;
  placeholder?: string;
  default_value?: string;
}

interface AnomalyBlock {
  kind: "anomaly";
  severity: AnomalySeverity;
  title: string;
  detail: string;
  affected_entities?: string[];
}

interface PermissionRequestBlock {
  kind: "permission_request";
  action: string;
  detail: string;
  counts: { label: string; value: string | number }[];
}

interface ErrorBlock {
  kind: "error";
  error_code: string;
  message: string;
  suggested_action: string;
  recoverable: boolean;
}

type AgentBlock =
  | MessageBlock
  | StatBlock
  | SummaryTableBlock
  | ClarifyingQuestionBlock
  | AnomalyBlock
  | PermissionRequestBlock
  | ErrorBlock;

interface AgentTurn {
  id: string;
  phase: Phase;
  blocks: AgentBlock[];
}

/* ---------------- Tool-call interrupt shape ---------------------------- */

type ToolName =
  | "save_shift_locations"
  | "save_schedule_assignments"
  | "create_single_shift_config"
  | "bulk_create_shift_configs";

interface ToolCallArg {
  label: string;
  // 'shift_config_uuid' triggers the readable-name resolution loader.
  kind: "text" | "shift_config_uuid" | "date_range" | "list";
  value: string;
  resolved?: string; // post-resolution display
  editable?: "date_range_only" | "none";
}

interface ToolCallInterrupt {
  id: string;
  tool: ToolName;
  description: string;
  args: ToolCallArg[];
}

const TOOL_HUMAN_NAME: Record<ToolName, string> = {
  save_shift_locations: "Create new shift locations",
  save_schedule_assignments: "Write shift assignments",
  create_single_shift_config: "Create one shift configuration",
  bulk_create_shift_configs: "Create multiple shift configurations",
};

/* ---------------- Top-level UI state ----------------------------------- */

type UploadState = "idle" | "uploading" | "uploaded" | "agent_unavailable";
type FlowSurface =
  | "agent_picker" // choose which agent to talk to
  | "pre_import" // upload zone (with optional resume card)
  | "scanning" // file uploaded, agent doing initial scan
  | "review" // HITL conversation rendering blocks
  | "tool_call" // a tool-call interrupt is pending (modal)
  | "saving" // operator approved final permission_request — agent writing
  | "complete" // extraction_complete reached
  | "agent_unavailable" // wrapper-level error
  | "report_compose" // report chat: empty prompt state
  | "report_clarify" // report chat: agent's clarifying turn
  | "report_preview" // report chat: generated report preview
  | "report_complete"; // report chat: saved

/* ---------------- Mock fixtures ---------------------------------------- */

const RESUME_MOCK = {
  uploadedAt: "2026-05-17 16:42",
  fileName: "april-2026-roster-v3.xlsx",
  phase: "requesting_context" as Phase,
  questionsLeft: 2,
};

const INITIAL_SCAN_TURN: AgentTurn = {
  id: "t-1",
  phase: "initial_scan",
  blocks: [
    {
      kind: "message",
      body:
        "I scanned **april-2026-roster-v3.xlsx**. Here's what I extracted. Confirm the details below or correct anything that looks wrong before I match employees and sites.",
    },
    { kind: "stat", label: "Pages", value: 4, icon: "file" },
    { kind: "stat", label: "Employees found", value: 75, icon: "users" },
    { kind: "stat", label: "Sites found", value: 6, icon: "map" },
    {
      kind: "stat",
      label: "Roster period",
      value: "1 Apr – 30 Apr 2026",
      icon: "calendar",
    },
    {
      kind: "summary_table",
      title: "Sites detected in the file",
      columns: [
        { key: "name", label: "Source name" },
        { key: "match", label: "Match in Jem" },
        { key: "zones", label: "Zones / sections" },
      ],
      rows: [
        { name: "Sandton City", match: "Sandton City", zones: "Floor 1, Floor 2" },
        { name: "Rosebank Mall", match: "Rosebank Mall", zones: "Mall, Parkade" },
        { name: "Carlton Centre", match: "Carlton Centre", zones: "Main" },
        { name: "OR Tambo Hub", match: "OR Tambo Logistics Hub", zones: "Dock A, Dock B" },
        { name: "V&A Waterfront", match: "V&A Waterfront Security", zones: "Quay 4, Quay 6" },
        { name: "Gateway Theatre", match: "— no match —", zones: "Cinema, Concourse" },
      ],
      footnote: "5 of 6 sites matched. 1 site needs a decision below.",
    },
    {
      kind: "summary_table",
      title: "Shift codes used in the file",
      columns: [
        { key: "code", label: "Code" },
        { key: "guess", label: "Most likely meaning" },
        { key: "count", label: "Rows" },
      ],
      rows: [
        { code: "M", guess: "Morning (06:00 – 14:00)", count: "32" },
        { code: "A", guess: "Afternoon (14:00 – 22:00)", count: "21" },
        { code: "N", guess: "Night (22:00 – 06:00)", count: "18" },
        { code: "OFF", guess: "Day off", count: "84" },
      ],
      footnote: "I'll confirm these meanings with you below.",
    },
    {
      kind: "anomaly",
      severity: "warning",
      title: "3 ambiguous employee names",
      detail:
        "Three rows match more than one employee in Jem on first-name + last-name. I'll ask you to pick the right one in the next step.",
      affected_entities: ["S. Mokoena", "J. Naidoo", "T. Khumalo"],
    },
    {
      kind: "anomaly",
      severity: "info",
      title: "8 weekend rows have a different cadence",
      detail:
        "Sat/Sun shifts on Rosebank Mall use only N (Night). I'll preserve this cadence in the assignments unless you tell me otherwise.",
    },
  ],
};

const CLARIFY_TURN: AgentTurn = {
  id: "t-2",
  phase: "requesting_context",
  blocks: [
    {
      kind: "message",
      body:
        "A few quick decisions before I match employees and write assignments. Each answer is required.",
    },
    {
      kind: "clarifying_question",
      question_id: "site_gateway",
      prompt: "Gateway Theatre wasn't in your Jem sites. What should I do?",
      input_type: "single_select",
      required: true,
      options: [
        {
          value: "create_new",
          label: "Create it as a new shift location",
          sublabel: "Recommended if Gateway Theatre is a real new site.",
        },
        {
          value: "map_existing",
          label: "Map it to an existing site",
          sublabel: "Use this if the file uses a different name for a site you already have.",
        },
        {
          value: "skip_site",
          label: "Skip — don't import rows for this site",
          sublabel: "Use this if rows under Gateway Theatre shouldn't land in Jem.",
        },
      ],
    },
    {
      kind: "clarifying_question",
      question_id: "confirm_shift_codes",
      prompt: "Do these shift code meanings look right?",
      input_type: "confirm",
      required: true,
      default_value: "yes",
    },
    {
      kind: "clarifying_question",
      question_id: "weekend_cadence",
      prompt:
        "Rosebank Mall weekends only have Night shifts. Should I keep this pattern, or apply your standard Mon-Sun cadence?",
      input_type: "single_select",
      required: true,
      options: [
        { value: "keep_source", label: "Keep what the file shows (Night only Sat/Sun)" },
        { value: "apply_standard", label: "Apply standard Mon-Sun cadence" },
      ],
    },
    {
      kind: "clarifying_question",
      question_id: "notes",
      prompt: "Anything else I should know? (optional)",
      input_type: "free_text",
      required: false,
      placeholder: "e.g. ignore J. Naidoo — on leave for April",
    },
  ],
};

const VERIFICATION_TURN: AgentTurn = {
  id: "t-3",
  phase: "verification",
  blocks: [
    {
      kind: "message",
      body:
        "All clarifications recorded. Here's the side-by-side preview of every row I'll write. Skip or correct any row before I ask for permission to save.",
    },
    {
      kind: "permission_request",
      action: "Write 72 shift assignments to Jem",
      detail:
        "Once you approve, I'll create 1 new shift location, 0 new shift configs, and 72 assignments across 6 sites for April 2026. You'll still approve each database write individually.",
      counts: [
        { label: "Assignments to create", value: 58 },
        { label: "Assignments to replace", value: 14 },
        { label: "Rows skipped", value: 3 },
        { label: "New shift locations", value: 1 },
        { label: "New shift configs", value: 0 },
      ],
    },
  ],
};

const COMPLETE_TURN: AgentTurn = {
  id: "t-4",
  phase: "extraction_complete",
  blocks: [
    {
      kind: "message",
      body:
        "Import complete. April 2026 roster is now live in Jem. Below is what changed.",
    },
    { kind: "stat", label: "Assignments created", value: 58 },
    { kind: "stat", label: "Assignments replaced", value: 14 },
    { kind: "stat", label: "Rows skipped", value: 3 },
    { kind: "stat", label: "New locations added", value: 1 },
  ],
};

/* ---------------- Per-row review fixtures (for the side-by-side) ------- */

interface ReviewRow {
  id: string;
  sourceLabel: string;
  sourceDetail: string;
  jemEmployee: string;
  jemSite: string;
  jemShift: string;
  jemDates: string;
  status: "ok" | "ambiguous" | "unmatched";
  ambiguityNote?: string;
}

const REVIEW_ROWS: ReviewRow[] = [
  {
    id: "r1",
    sourceLabel: "Row 12 · S. Mokoena",
    sourceDetail: "Sandton City · M · 01–30 Apr",
    jemEmployee: "Sipho Mokoena (EMP-1042)",
    jemSite: "Sandton City",
    jemShift: "M (Morning, 06:00–14:00)",
    jemDates: "01 Apr – 30 Apr 2026",
    status: "ambiguous",
    ambiguityNote: "2 matches: Sipho Mokoena (EMP-1042) and Sibongile Mokoena (EMP-2387).",
  },
  {
    id: "r2",
    sourceLabel: "Row 13 · J. Naidoo",
    sourceDetail: "Rosebank Mall · A · 01–30 Apr",
    jemEmployee: "Jenny Naidoo (EMP-1188)",
    jemSite: "Rosebank Mall",
    jemShift: "A (Afternoon, 14:00–22:00)",
    jemDates: "01 Apr – 30 Apr 2026",
    status: "ambiguous",
    ambiguityNote: "2 matches: Jenny Naidoo (EMP-1188) and Jaden Naidoo (EMP-2901).",
  },
  {
    id: "r3",
    sourceLabel: "Row 14 · T. Khumalo",
    sourceDetail: "Carlton Centre · N · 01–30 Apr",
    jemEmployee: "Themba Khumalo (EMP-1305)",
    jemSite: "Carlton Centre",
    jemShift: "N (Night, 22:00–06:00)",
    jemDates: "01 Apr – 30 Apr 2026",
    status: "ambiguous",
    ambiguityNote: "2 matches: Themba Khumalo and Thandi Khumalo.",
  },
  {
    id: "r4",
    sourceLabel: "Row 22 · M. Dlamini",
    sourceDetail: "OR Tambo Hub · M · 01–30 Apr",
    jemEmployee: "Mandla Dlamini (EMP-1567)",
    jemSite: "OR Tambo Logistics Hub",
    jemShift: "M (Morning, 06:00–14:00)",
    jemDates: "01 Apr – 30 Apr 2026",
    status: "ok",
  },
  {
    id: "r5",
    sourceLabel: "Row 38 · P. Smith",
    sourceDetail: "Gateway Theatre · M · 01–30 Apr",
    jemEmployee: "— no employee match —",
    jemSite: "Gateway Theatre (new)",
    jemShift: "M (Morning, 06:00–14:00)",
    jemDates: "01 Apr – 30 Apr 2026",
    status: "unmatched",
    ambiguityNote: "P. Smith doesn't match any employee in Jem.",
  },
];

/* ---------------- Tool-call interrupts (fixtures) ---------------------- */

const TOOL_CALLS: ToolCallInterrupt[] = [
  {
    id: "tc-1",
    tool: "save_shift_locations",
    description: "Create the new Gateway Theatre site.",
    args: [
      { label: "Site name", kind: "text", value: "Gateway Theatre", editable: "none" },
      { label: "Zones", kind: "list", value: "Cinema, Concourse", editable: "none" },
      {
        label: "Effective dates",
        kind: "date_range",
        value: "2026-04-01 → 2026-04-30",
        editable: "date_range_only",
      },
    ],
  },
  {
    id: "tc-2",
    tool: "save_schedule_assignments",
    description: "Write 72 shift assignments for April 2026.",
    args: [
      { label: "Employees", kind: "list", value: "72 employees", editable: "none" },
      {
        label: "Shift configs",
        kind: "shift_config_uuid",
        value: "cfg_4f9b2a, cfg_9c1d3e, cfg_aa00bd",
        editable: "none",
      },
      {
        label: "Effective dates",
        kind: "date_range",
        value: "2026-04-01 → 2026-04-30",
        editable: "date_range_only",
      },
    ],
  },
];

/* ---------------- Post-import fixtures --------------------------------- */

interface AssignmentRow {
  employee: string;
  empNumber: string;
  site: string;
  weekday: string;
  saturday: string;
  sunday: string;
}

const POST_ASSIGNMENTS: AssignmentRow[] = [
  { employee: "Sipho Mokoena", empNumber: "EMP-1042", site: "Sandton City", weekday: "M", saturday: "OFF", sunday: "OFF" },
  { employee: "Jenny Naidoo", empNumber: "EMP-1188", site: "Rosebank Mall", weekday: "A", saturday: "N", sunday: "N" },
  { employee: "Themba Khumalo", empNumber: "EMP-1305", site: "Carlton Centre", weekday: "N", saturday: "N", sunday: "OFF" },
  { employee: "Mandla Dlamini", empNumber: "EMP-1567", site: "OR Tambo Logistics Hub", weekday: "M", saturday: "M", sunday: "OFF" },
];

interface UnmatchedRow {
  id: string;
  name: string;
  source: string;
  reason: string;
}

const UNMATCHED: UnmatchedRow[] = [
  { id: "u1", name: "P. Smith", source: "Gateway Theatre · Row 38", reason: "No employee in Jem matches this name." },
  { id: "u2", name: "K. Mahlangu", source: "Sandton City · Row 41", reason: "No employee in Jem matches this name." },
];

interface DiffRow {
  id: string;
  bucket: "added" | "removed" | "moved";
  employee: string;
  before: string;
  after: string;
}

const DIFF_ROWS: DiffRow[] = [
  { id: "d1", bucket: "added", employee: "Mandla Dlamini", before: "—", after: "OR Tambo Hub · M" },
  { id: "d2", bucket: "added", employee: "Pretty Mahlangu", before: "—", after: "Carlton Centre · A" },
  { id: "d3", bucket: "moved", employee: "Sipho Mokoena", before: "Rosebank Mall · M", after: "Sandton City · M" },
  { id: "d4", bucket: "removed", employee: "Lerato Khoza", before: "Carlton Centre · N", after: "—" },
];

/* ---------------- Report-chat fixtures --------------------------------- */

// Suggested-prompt chips on the empty compose state. Operators can click one
// to seed the prompt input; "like Claude's empty state."
const REPORT_PROMPT_EXAMPLES: { id: string; label: string; hint: string }[] = [
  { id: "ot-by-site",    label: "Overtime by site for April",            hint: "Compare OT hours and cost across sites" },
  { id: "hours-quarter", label: "Hours worked per employee last quarter", hint: "Per-employee totals across the last 90 days" },
  { id: "late-clockins", label: "Late clock-ins by site last month",      hint: "Counts of clock-ins after scheduled start" },
  { id: "headcount",     label: "Active headcount by site",               hint: "Snapshot of active employees per site" },
];

// The clarifying turn the report agent emits after the first prompt.
// Reuses the same AgentBlock contract as the roster agent — block-renderer
// stays identical. Lifts H4 (Consistency) across the two surfaces.
const REPORT_CLARIFY_TURN: AgentTurn = {
  id: "report-clarify-1",
  phase: "requesting_context",
  blocks: [
    {
      kind: "message",
      body: "Got it — overtime by site for April 2026. A few quick choices and I'll build it.",
    },
    {
      kind: "clarifying_question",
      question_id: "rpt_date_range",
      prompt: "Date range",
      input_type: "single_select",
      required: true,
      default_value: "april_2026",
      options: [
        { value: "april_2026",     label: "April 2026 only",       sublabel: "1 Apr – 30 Apr 2026" },
        { value: "march_april",    label: "March + April 2026",    sublabel: "1 Mar – 30 Apr 2026" },
        { value: "ytd",            label: "Year to date",           sublabel: "1 Jan – 19 May 2026" },
        { value: "custom",         label: "Custom range",           sublabel: "Pick exact dates" },
      ],
    },
    {
      kind: "clarifying_question",
      question_id: "rpt_metrics",
      prompt: "Which metrics?",
      input_type: "multi_select",
      required: true,
      options: [
        { value: "ot_hours",   label: "Total OT hours" },
        { value: "ot_cost",    label: "OT cost (R)" },
        { value: "ot_events",  label: "Number of OT events" },
        { value: "ot_avg_emp", label: "Average OT per employee" },
      ],
    },
    {
      kind: "clarifying_question",
      question_id: "rpt_group_by",
      prompt: "Group rows by",
      input_type: "single_select",
      required: true,
      default_value: "site",
      options: [
        { value: "site",          label: "Site",                  sublabel: "One row per site" },
        { value: "site_employee", label: "Site → employee",       sublabel: "Expandable per-employee breakdown" },
      ],
    },
    {
      kind: "clarifying_question",
      question_id: "rpt_weekends",
      prompt: "Include weekends?",
      input_type: "single_select",
      required: false,
      default_value: "yes",
      options: [
        { value: "yes",   label: "Yes" },
        { value: "sat",   label: "Saturday only" },
        { value: "no",    label: "Weekdays only" },
      ],
    },
    {
      kind: "permission_request",
      action: "Generate this report",
      detail:
        "I'll run the query against the time-attendance database and show you a preview. Nothing is saved until you confirm.",
      counts: [
        { label: "Sites",       value: 6 },
        { label: "Employees",   value: 142 },
        { label: "Date range",  value: "April 2026" },
        { label: "Metrics",     value: 4 },
      ],
    },
  ],
};

// The generated report preview after Approve.
interface ReportPreviewRow {
  site: string;
  otHours: string;
  otCost: string;
  otEvents: number;
  otAvgEmp: string;
}

const REPORT_PREVIEW_ROWS: ReportPreviewRow[] = [
  { site: "Sandton City",     otHours: "184.5", otCost: "R 27 675", otEvents: 41, otAvgEmp: "7.4h" },
  { site: "Rosebank Mall",    otHours: "156.0", otCost: "R 23 400", otEvents: 38, otAvgEmp: "6.5h" },
  { site: "Carlton Centre",   otHours: "121.5", otCost: "R 18 225", otEvents: 29, otAvgEmp: "5.3h" },
  { site: "OR Tambo Hub",     otHours: "98.0",  otCost: "R 14 700", otEvents: 22, otAvgEmp: "4.5h" },
  { site: "Gateway Theatre",  otHours: "62.0",  otCost: "R 9 300",  otEvents: 17, otAvgEmp: "3.1h" },
  { site: "Menlyn Park",      otHours: "44.5",  otCost: "R 6 675",  otEvents: 12, otAvgEmp: "2.2h" },
];

// Saved reports — picker's "what have I built before" surface.
// Rerun-fresh model: each entry is a query, not a snapshot. The dateScope
// disambiguates relative ("rolling") from absolute ("fixed") so that
// "Last month's overtime" doesn't drift in meaning over time.
type SavedReportScope = "fixed" | "rolling";

interface SavedReport {
  id: string;
  title: string;
  prompt: string;
  savedAt: string;
  scope: SavedReportScope;
  dateLabel: string;
  groupBy: string;
  metrics: string[];
}

const MOCK_SAVED_REPORTS: SavedReport[] = [
  {
    id: "rep-1",
    title: "Overtime by site — March 2026",
    prompt: "Show me overtime by site for March 2026",
    savedAt: "2026-04-04",
    scope: "fixed",
    dateLabel: "March 2026",
    groupBy: "Site",
    metrics: ["OT hours", "OT cost", "# OT events", "Avg per employee"],
  },
  {
    id: "rep-2",
    title: "Late clock-ins — rolling 30 days",
    prompt: "Late clock-ins by site for the last month",
    savedAt: "2026-05-02",
    scope: "rolling",
    dateLabel: "Rolling 30 days",
    groupBy: "Site",
    metrics: ["# late clock-ins", "Avg delay (min)"],
  },
  {
    id: "rep-3",
    title: "Active headcount by site",
    prompt: "Active headcount by site",
    savedAt: "2026-05-12",
    scope: "rolling",
    dateLabel: "As of today",
    groupBy: "Site",
    metrics: ["Active headcount"],
  },
];

/* ====================================================================== */
/*  Block renderer — maps each typed block to a jem-ui primitive          */
/* ====================================================================== */

interface BlockAnswers {
  [questionId: string]: string;
}

interface BlockRendererProps {
  block: AgentBlock;
  answers: BlockAnswers;
  onAnswerChange: (id: string, value: string) => void;
}

function StatTile({ stat }: { stat: StatBlock }) {
  const Icon = ({
    users: UsersIcon,
    map: MapPin,
    calendar: Calendar,
    file: FileText,
  } as const)[stat.icon ?? "file"];
  return (
    <div className="rounded-lg border border-greyscale-border bg-white p-md">
      <div className="flex items-center gap-xs text-greyscale-text-caption mb-xxs">
        <Icon size={14} />
        <span className="text-xs font-semibold uppercase tracking-wide">{stat.label}</span>
      </div>
      <p className="text-2xl font-heading font-semibold text-greyscale-text-title leading-none">
        {stat.value}
      </p>
      {stat.hint && (
        <p className="text-xs text-greyscale-text-caption mt-xxs">{stat.hint}</p>
      )}
    </div>
  );
}

function SummaryTable({ block }: { block: SummaryTableBlock }) {
  const columns: ColumnDef<Record<string, string>>[] = block.columns.map((c) => ({
    accessorKey: c.key,
    header: ({ column }) => <DataTableColumnHeader column={column} title={c.label} />,
    cell: ({ row }) => {
      const value = row.getValue(c.key) as string;
      if (value === "— no match —") {
        return <Tag variant="failed" size="sm">{value}</Tag>;
      }
      return <span className="text-sm text-greyscale-text-body">{value}</span>;
    },
  }));
  return (
    <div className="rounded-lg border border-greyscale-border bg-white p-md">
      <div className="flex items-start justify-between gap-sm mb-sm">
        <div>
          <h4 className="text-sm font-semibold text-greyscale-text-title">{block.title}</h4>
          {block.caption && (
            <p className="text-xs text-greyscale-text-caption mt-xxxs">{block.caption}</p>
          )}
        </div>
      </div>
      <DataTable columns={columns} data={block.rows} />
      {block.footnote && (
        <p className="text-xs text-greyscale-text-caption mt-sm flex items-start gap-xs">
          <Info size={12} className="mt-[2px] shrink-0" />
          {block.footnote}
        </p>
      )}
    </div>
  );
}

function AnomalyAlert({ block }: { block: AnomalyBlock }) {
  const variantClass =
    block.severity === "error"
      ? "border-secondary-pink-300 bg-secondary-pink-50"
      : block.severity === "warning"
        ? "border-orange-300 bg-orange-50"
        : "border-primary-navy-200 bg-primary-navy-50";
  const Icon =
    block.severity === "error" ? CircleAlert : block.severity === "warning" ? AlertTriangle : Info;
  const iconClass =
    block.severity === "error"
      ? "text-secondary-pink-900"
      : block.severity === "warning"
        ? "text-orange-600"
        : "text-primary-navy-900";
  return (
    <div className={cn("rounded-lg border p-md", variantClass)}>
      <div className="flex items-start gap-sm">
        <Icon size={18} className={cn("shrink-0 mt-[2px]", iconClass)} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-greyscale-text-title">{block.title}</p>
          <p className="text-sm text-greyscale-text-body mt-xxxs">{block.detail}</p>
          {block.affected_entities && block.affected_entities.length > 0 && (
            <div className="flex flex-wrap gap-xs mt-xs">
              {block.affected_entities.map((e) => (
                <Tag key={e} variant="drafted" size="sm">
                  {e}
                </Tag>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PermissionRequest({
  block,
  onApprove,
  onReject,
}: {
  block: PermissionRequestBlock;
  onApprove: () => void;
  onReject: (reason: string) => void;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  return (
    <div className="rounded-lg border-2 border-secondary-pink-300 bg-secondary-pink-50 p-lg">
      <div className="flex items-start gap-sm mb-md">
        <ShieldAlert size={20} className="text-secondary-pink-900 mt-xxxs shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-greyscale-text-title">
            Permission required: {block.action}
          </h3>
          <p className="text-sm text-greyscale-text-body mt-xxxs">{block.detail}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-xs mb-md">
        {block.counts.map((c) => (
          <div key={c.label} className="rounded-md bg-white border border-greyscale-border p-sm">
            <p className="text-xs text-greyscale-text-caption">{c.label}</p>
            <p className="text-lg font-semibold text-greyscale-text-title">{c.value}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-sm">
        <Button onClick={onApprove} leftIcon={<Check size={16} />}>
          Approve and continue
        </Button>
        <Button variant="outline" onClick={() => setRejectOpen(true)} leftIcon={<X size={16} />}>
          Reject
        </Button>
        <p className="text-xs text-greyscale-text-caption">
          Nothing is written until you approve each individual database action in the next step.
        </p>
      </div>
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject this import?</DialogTitle>
            <DialogDescription>
              I'll keep your in-flight corrections and take you back to the review screen.
              An optional reason helps the team learn from rejected imports.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="reject-reason">Reason (optional)</Label>
            <Textarea
              id="reject-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. wrong file uploaded — needs to be the May version"
              className="mt-xs"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setRejectOpen(false);
                onReject(reason);
              }}
            >
              Reject and return to review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClarifyingQuestion({
  block,
  value,
  onChange,
}: {
  block: ClarifyingQuestionBlock;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="rounded-lg border border-greyscale-border bg-white p-md">
      <div className="flex items-start gap-xs mb-sm">
        <CircleHelp size={16} className="text-primary-navy-900 mt-[3px] shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-greyscale-text-title">
            {block.prompt}
            {block.required && (
              <span className="text-secondary-pink-900 ml-xxs">*</span>
            )}
          </p>
        </div>
      </div>
      {block.input_type === "free_text" && (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={block.placeholder}
          rows={2}
        />
      )}
      {block.input_type === "single_select" && block.options && (
        <RadioGroup value={value} onValueChange={onChange} className="flex flex-col gap-xs">
          {block.options.map((o) => (
            <Label
              key={o.value}
              htmlFor={`${block.question_id}-${o.value}`}
              className="flex items-start gap-sm rounded-md border border-greyscale-border p-sm cursor-pointer hover:bg-neutral-50"
            >
              <RadioGroupItem value={o.value} id={`${block.question_id}-${o.value}`} className="mt-xxs" />
              <span className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-greyscale-text-title block">{o.label}</span>
                {o.sublabel && (
                  <span className="text-xs text-greyscale-text-caption block mt-xxxs">{o.sublabel}</span>
                )}
              </span>
            </Label>
          ))}
        </RadioGroup>
      )}
      {block.input_type === "multi_select" && block.options && (
        <div className="flex flex-col gap-xs">
          {block.options.map((o) => {
            const checked = (value || "").split(",").includes(o.value);
            return (
              <Label
                key={o.value}
                className="flex items-start gap-sm rounded-md border border-greyscale-border p-sm cursor-pointer hover:bg-neutral-50"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(next) => {
                    const set = new Set((value || "").split(",").filter(Boolean));
                    if (next) set.add(o.value);
                    else set.delete(o.value);
                    onChange(Array.from(set).join(","));
                  }}
                />
                <span className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-greyscale-text-title block">{o.label}</span>
                  {o.sublabel && (
                    <span className="text-xs text-greyscale-text-caption block mt-xxxs">{o.sublabel}</span>
                  )}
                </span>
              </Label>
            );
          })}
        </div>
      )}
      {block.input_type === "confirm" && (
        <RadioGroup value={value} onValueChange={onChange} className="flex items-center gap-md">
          <div className="flex items-center gap-xs">
            <RadioGroupItem value="yes" id={`${block.question_id}-yes`} />
            <Label htmlFor={`${block.question_id}-yes`}>Yes</Label>
          </div>
          <div className="flex items-center gap-xs">
            <RadioGroupItem value="no" id={`${block.question_id}-no`} />
            <Label htmlFor={`${block.question_id}-no`}>No</Label>
          </div>
        </RadioGroup>
      )}
    </div>
  );
}

function MessageBlockView({ block }: { block: MessageBlock }) {
  // Minimal markdown: bold using **...**
  const parts = block.body.split(/(\*\*[^*]+\*\*)/g);
  return (
    <div className="rounded-lg bg-primary-navy-50 border border-primary-navy-200 p-md flex items-start gap-sm">
      <Bot size={18} className="text-primary-navy-900 mt-xxxs shrink-0" />
      <p className="text-sm text-greyscale-text-body leading-relaxed">
        {parts.map((p, i) =>
          p.startsWith("**") && p.endsWith("**") ? (
            <strong key={i} className="font-semibold text-greyscale-text-title">
              {p.slice(2, -2)}
            </strong>
          ) : (
            <span key={i}>{p}</span>
          ),
        )}
      </p>
    </div>
  );
}

function BlockRenderer({ block, answers, onAnswerChange }: BlockRendererProps) {
  switch (block.kind) {
    case "message":
      return <MessageBlockView block={block} />;
    case "stat":
      return <StatTile stat={block} />;
    case "summary_table":
      return <SummaryTable block={block} />;
    case "anomaly":
      return <AnomalyAlert block={block} />;
    case "clarifying_question":
      return (
        <ClarifyingQuestion
          block={block}
          value={answers[block.question_id] ?? block.default_value ?? ""}
          onChange={(v) => onAnswerChange(block.question_id, v)}
        />
      );
    default:
      return null;
  }
}

/* ====================================================================== */
/*  Side-by-side review row component                                     */
/* ====================================================================== */

interface ReviewRowItemProps {
  row: ReviewRow;
  skipped: boolean;
  onToggleSkip: () => void;
  correctedTo?: string;
  onCorrect: (employeeId: string) => void;
}

function ReviewRowItem({ row, skipped, onToggleSkip, correctedTo, onCorrect }: ReviewRowItemProps) {
  const [correctOpen, setCorrectOpen] = useState(false);
  const statusTag =
    row.status === "ok" ? (
      <Tag variant="success" size="sm">Matched</Tag>
    ) : row.status === "ambiguous" ? (
      <Tag variant="drafted" size="sm">Ambiguous</Tag>
    ) : (
      <Tag variant="failed" size="sm">Unmatched</Tag>
    );
  return (
    <div
      className={cn(
        "rounded-lg border p-md grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto] gap-sm md:gap-md items-start",
        skipped ? "border-greyscale-border bg-neutral-50 opacity-60" : "border-greyscale-border bg-white",
      )}
    >
      {/* Source side */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption mb-xxs">
          Source file
        </p>
        <p className="text-sm font-semibold text-greyscale-text-title">{row.sourceLabel}</p>
        <p className="text-xs text-greyscale-text-body mt-xxxs">{row.sourceDetail}</p>
      </div>
      <ArrowRight size={16} className="hidden md:block text-greyscale-text-caption mt-md" />
      {/* Jem side */}
      <div>
        <div className="flex items-center gap-xs mb-xxs">
          <p className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption">
            Jem will create
          </p>
          {statusTag}
        </div>
        <p className="text-sm font-semibold text-greyscale-text-title">{correctedTo ?? row.jemEmployee}</p>
        <p className="text-xs text-greyscale-text-body mt-xxxs">
          {row.jemSite} · {row.jemShift}
        </p>
        <p className="text-xs text-greyscale-text-caption mt-xxxs">{row.jemDates}</p>
        {row.ambiguityNote && !skipped && (
          <p className="text-xs text-orange-600 mt-xs flex items-start gap-xs">
            <AlertTriangle size={12} className="mt-[2px] shrink-0" />
            {row.ambiguityNote}
          </p>
        )}
      </div>
      {/* Actions */}
      <div className="flex flex-col items-end gap-xs">
        {row.status !== "ok" && !skipped && (
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Pencil size={14} />}
            onClick={() => setCorrectOpen(true)}
          >
            Correct
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleSkip}
          leftIcon={skipped ? <RotateCcw size={14} /> : <X size={14} />}
        >
          {skipped ? "Include" : "Skip"}
        </Button>
      </div>
      <Dialog open={correctOpen} onOpenChange={setCorrectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pick the correct employee</DialogTitle>
            <DialogDescription>{row.ambiguityNote}</DialogDescription>
          </DialogHeader>
          <RadioGroup
            value={skipped ? "__skip__" : (correctedTo ?? row.jemEmployee)}
            onValueChange={(v) => onCorrect(v)}
            className="flex flex-col gap-xs mt-md"
          >
            <Label className="flex items-start gap-sm rounded-md border border-greyscale-border p-sm cursor-pointer">
              <RadioGroupItem value={row.jemEmployee} id={`${row.id}-a`} />
              <span>
                <span className="text-sm font-semibold block">{row.jemEmployee}</span>
                <span className="text-xs text-greyscale-text-caption block">Recommended match</span>
              </span>
            </Label>
            <Label className="flex items-start gap-sm rounded-md border border-greyscale-border p-sm cursor-pointer">
              <RadioGroupItem
                value={
                  row.ambiguityNote?.split("and")[1]?.replace(/[.()]/g, "").trim() ??
                  "Alternative employee"
                }
                id={`${row.id}-b`}
              />
              <span>
                <span className="text-sm font-semibold block">
                  {row.ambiguityNote?.split("and")[1]?.replace(/[.()]/g, "").trim() ?? "Alternative match"}
                </span>
                <span className="text-xs text-greyscale-text-caption block">Other candidate</span>
              </span>
            </Label>
            <Label className="flex items-start gap-sm rounded-md border border-greyscale-border p-sm cursor-pointer">
              <RadioGroupItem value="__skip__" id={`${row.id}-skip`} />
              <span>
                <span className="text-sm font-semibold block">Skip this row</span>
                <span className="text-xs text-greyscale-text-caption block">
                  Don't import this assignment.
                </span>
              </span>
            </Label>
          </RadioGroup>
          <DialogFooter>
            <Button onClick={() => setCorrectOpen(false)}>Save choice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ====================================================================== */
/*  Tool-call approval Dialog                                              */
/* ====================================================================== */

interface ToolCallDialogProps {
  call: ToolCallInterrupt;
  onApprove: (editedDates?: string) => void;
  onReject: (reason: string) => void;
  onClose: () => void;
}

function ToolCallDialog({ call, onApprove, onReject, onClose }: ToolCallDialogProps) {
  const [resolving, setResolving] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedDates, setEditedDates] = useState("");
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");

  // Mock UUID resolution. Real impl: query_shift_configs_for_location.
  // Per resolved decision #9, the UI BLOCKS on this before showing the panel.
  useMemo(() => {
    const t = setTimeout(() => setResolving(false), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.id]);

  const resolvedArgs: ToolCallArg[] = call.args.map((a) =>
    a.kind === "shift_config_uuid"
      ? {
          ...a,
          resolved:
            "M (Morning Shift, 06:00–14:00), A (Afternoon Shift, 14:00–22:00), N (Night Shift, 22:00–06:00)",
        }
      : a,
  );

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <div className="flex items-center gap-xs">
            <ShieldAlert size={18} className="text-secondary-pink-900" />
            <DialogTitle>Approve database write</DialogTitle>
          </div>
          <DialogDescription>
            <span className="font-semibold text-greyscale-text-title">
              {TOOL_HUMAN_NAME[call.tool]}
            </span>{" "}
            — {call.description}
          </DialogDescription>
        </DialogHeader>

        {resolving ? (
          <div className="space-y-sm py-md">
            <div className="flex items-center gap-xs text-sm text-greyscale-text-caption">
              <Skeleton className="h-4 w-4 rounded-full" />
              <span>Resolving shift configurations…</span>
            </div>
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </div>
        ) : !rejectMode ? (
          <div className="space-y-sm py-md">
            <div className="rounded-md border border-greyscale-border bg-neutral-50">
              {resolvedArgs.map((a, i) => (
                <div
                  key={a.label}
                  className={cn(
                    "px-md py-sm grid grid-cols-[140px_1fr_auto] gap-sm items-start",
                    i > 0 && "border-t border-greyscale-border",
                  )}
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption mt-xxxs">
                    {a.label}
                  </p>
                  <p className="text-sm text-greyscale-text-body">
                    {a.kind === "date_range" && editing ? (
                      <InputField
                        value={editedDates || a.value}
                        onChange={(e) => setEditedDates(e.target.value)}
                        className="max-w-[260px]"
                      />
                    ) : (
                      a.resolved ?? a.value
                    )}
                  </p>
                  {a.editable === "date_range_only" ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing((e) => !e)}
                      leftIcon={<Pencil size={12} />}
                    >
                      {editing ? "Done" : "Edit"}
                    </Button>
                  ) : (
                    <Tag variant="drafted" size="sm">Read-only</Tag>
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-greyscale-text-caption">
              You can only edit the effective dates here. To change the site, shift configuration,
              or employee list, reject this write and re-open the review screen.
            </p>
          </div>
        ) : (
          <div className="space-y-sm py-md">
            <Label htmlFor="tc-reject-reason">Reason (optional)</Label>
            <Textarea
              id="tc-reject-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. wrong site — should be a sub-zone of Rosebank"
              rows={3}
            />
          </div>
        )}

        <DialogFooter>
          {!rejectMode ? (
            <>
              <Button variant="outline" onClick={() => setRejectMode(true)}>
                Reject
              </Button>
              <Button onClick={() => onApprove(editing ? editedDates || undefined : undefined)} disabled={resolving}>
                Approve
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setRejectMode(false)}>
                Back
              </Button>
              <Button variant="destructive" onClick={() => onReject(reason)}>
                Confirm reject
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ====================================================================== */
/*  Agent picker — entry to a roster or report conversation               */
/* ====================================================================== */

interface AgentPickerProps {
  onPickRoster: () => void;
  onPickReport: () => void;
  savedReports: SavedReport[];
  onOpenSaved: (report: SavedReport) => void;
  onDeleteSaved: (id: string) => void;
}

function AgentPicker({
  onPickRoster,
  onPickReport,
  savedReports,
  onOpenSaved,
  onDeleteSaved,
}: AgentPickerProps) {
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const target = savedReports.find((r) => r.id === confirmDeleteId) ?? null;

  return (
    <div className="space-y-md">
      <div className="rounded-lg border border-greyscale-border bg-white p-lg">
        <div className="flex items-center gap-sm mb-xs">
          <Bot size={18} className="text-primary-navy-900" />
          <p className="text-sm font-semibold text-greyscale-text-title">
            What do you want to do?
          </p>
        </div>
        <p className="text-xs text-greyscale-text-caption mb-md">
          Pick the agent that matches the task. You can switch agents at any time.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
          <button
            type="button"
            onClick={onPickRoster}
            className="text-left rounded-lg border border-greyscale-border bg-white p-md hover:border-primary-navy-900 hover:bg-neutral-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-navy-900 focus:ring-offset-2"
          >
            <div className="flex items-center gap-sm mb-xs">
              <div className="rounded-md bg-primary-navy-50 p-xs">
                <UploadIcon size={18} className="text-primary-navy-900" />
              </div>
              <p className="text-sm font-semibold text-greyscale-text-title">
                Import a roster
              </p>
            </div>
            <p className="text-xs text-greyscale-text-caption mb-sm">
              Upload an Excel, CSV, or PDF roster. The agent parses, asks clarifying questions,
              and writes shift assignments after you approve.
            </p>
            <div className="flex items-center gap-xs text-xs text-primary-navy-900 font-medium">
              <span>Start a roster import</span>
              <ChevronRight size={14} />
            </div>
          </button>

          <button
            type="button"
            onClick={onPickReport}
            className="text-left rounded-lg border border-greyscale-border bg-white p-md hover:border-primary-navy-900 hover:bg-neutral-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-navy-900 focus:ring-offset-2"
          >
            <div className="flex items-center gap-sm mb-xs">
              <div className="rounded-md bg-secondary-pink-50 p-xs">
                <Sparkles size={18} className="text-secondary-pink-900" />
              </div>
              <p className="text-sm font-semibold text-greyscale-text-title">
                Generate a report from text
              </p>
              <Tag variant="processing" size="sm">New</Tag>
            </div>
            <p className="text-xs text-greyscale-text-caption mb-sm">
              Describe the report you want in plain English. The agent asks a few questions,
              shows you a preview, and saves it under Saved custom reports below.
            </p>
            <div className="flex items-center gap-xs text-xs text-primary-navy-900 font-medium">
              <span>Start a report chat</span>
              <ChevronRight size={14} />
            </div>
          </button>
        </div>
      </div>

      {/* Saved custom reports — rerun-fresh model. Click rerun to regenerate
          against current data. Rolling reports re-resolve their date window;
          fixed reports stay pinned to the absolute range chosen at save. */}
      <div className="rounded-lg border border-greyscale-border bg-white p-lg">
        <div className="flex items-center justify-between gap-sm mb-xs flex-wrap">
          <div className="flex items-center gap-sm">
            <History size={18} className="text-primary-navy-900" />
            <p className="text-sm font-semibold text-greyscale-text-title">
              Saved custom reports
            </p>
            {savedReports.length > 0 && (
              <Tag variant="drafted" size="sm">{savedReports.length}</Tag>
            )}
          </div>
          <p className="text-xs text-greyscale-text-caption">
            Rerun regenerates against current data.
          </p>
        </div>

        {savedReports.length === 0 ? (
          <div className="rounded-md border border-dashed border-greyscale-border bg-neutral-50 p-md text-center">
            <FileBarChart size={20} className="text-greyscale-text-caption mx-auto mb-xs" />
            <p className="text-xs text-greyscale-text-caption">
              You haven't saved any custom reports yet. Generate one above to see it here.
            </p>
          </div>
        ) : (
          <div className="space-y-xs mt-sm">
            {savedReports.map((r) => (
              <div
                key={r.id}
                className="rounded-md border border-greyscale-border bg-white p-md flex items-start gap-sm hover:border-primary-navy-900 transition-colors"
              >
                <FileBarChart
                  size={18}
                  className="text-primary-navy-900 mt-xxxs shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-xs flex-wrap mb-xxxs">
                    <p className="text-sm font-semibold text-greyscale-text-title">
                      {r.title}
                    </p>
                    <Tag
                      variant={r.scope === "rolling" ? "processing" : "drafted"}
                      size="sm"
                    >
                      {r.dateLabel}
                    </Tag>
                  </div>
                  <p className="text-xs text-greyscale-text-caption truncate">
                    “{r.prompt}”
                  </p>
                  <p className="text-xs text-greyscale-text-caption mt-xxxs">
                    Grouped by {r.groupBy} · {r.metrics.length} metric
                    {r.metrics.length === 1 ? "" : "s"} · Saved {r.savedAt}
                  </p>
                </div>
                <div className="flex items-center gap-xs shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onOpenSaved(r)}
                    leftIcon={<RotateCcw size={14} />}
                  >
                    Rerun
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setConfirmDeleteId(r.id)}
                    leftIcon={<X size={14} />}
                    aria-label={`Delete ${r.title}`}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog
        open={!!confirmDeleteId}
        onOpenChange={(open) => !open && setConfirmDeleteId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this saved report?</DialogTitle>
            <DialogDescription>
              {target ? `“${target.title}” will be removed.` : ""} The underlying time-attendance
              data is not affected — only the saved query.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmDeleteId) onDeleteSaved(confirmDeleteId);
                setConfirmDeleteId(null);
              }}
            >
              Delete report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ====================================================================== */
/*  Report chat — phase rail, compose, clarify, preview, complete         */
/* ====================================================================== */

type ReportPhase = "compose" | "clarify" | "preview" | "complete";

function ReportPhaseRail({ phase, title }: { phase: ReportPhase; title: string }) {
  const phaseLabel: Record<ReportPhase, string> = {
    compose: "1. Composing prompt",
    clarify: "2. Clarifying scope",
    preview: "3. Preview",
    complete: "4. Saved",
  };
  return (
    <div className="flex items-center justify-between gap-sm rounded-md bg-white border border-greyscale-border px-md py-sm">
      <div className="flex items-center gap-xs min-w-0">
        <FileBarChart size={14} className="text-greyscale-text-caption shrink-0" />
        <p className="text-xs text-greyscale-text-caption truncate">
          <span className="font-semibold text-greyscale-text-title">{title}</span>
        </p>
      </div>
      <Tag variant="processing" size="sm">
        {phaseLabel[phase]}
      </Tag>
    </div>
  );
}

// Sticky bottom chat input. Decorative in the mock — sends post a transient
// message into the conversation history but don't drive a new agent turn.
interface ReportChatInputProps {
  placeholder: string;
  onSend?: (text: string) => void;
  helper?: string;
}

function ReportChatInput({ placeholder, onSend, helper }: ReportChatInputProps) {
  const [value, setValue] = useState("");
  const submit = () => {
    if (!value.trim()) return;
    onSend?.(value.trim());
    setValue("");
  };
  return (
    <div className="sticky bottom-0 -mx-md sm:mx-0 bg-white border-t border-greyscale-border p-md rounded-b-lg">
      <div className="flex items-end gap-sm">
        <div className="flex-1">
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            rows={2}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submit();
              }
            }}
          />
          {helper && (
            <p className="mt-xxs text-xs text-greyscale-text-caption">{helper}</p>
          )}
        </div>
        <Button
          onClick={submit}
          disabled={!value.trim()}
          leftIcon={<Send size={14} />}
        >
          Send
        </Button>
      </div>
    </div>
  );
}

/* ====================================================================== */
/*  Main CustomReports component                                          */
/* ====================================================================== */

export interface CustomReportsProps {
  /** Drives state variants for QA/demo. */
  variant?:
    | "default"
    | "resume"
    | "agent_unavailable"
    | "first_ever_employer"
    | "report_chat";
}

export function CustomReports({ variant = "default" }: CustomReportsProps) {
  const [surface, setSurface] = useState<FlowSurface>(
    variant === "agent_unavailable"
      ? "agent_unavailable"
      : variant === "report_chat"
        ? "report_compose"
        : variant === "resume"
          ? "pre_import"
          : "agent_picker",
  );
  const [uploadState, setUploadState] = useState<UploadState>("idle");
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState<string | undefined>();
  const [answers, setAnswers] = useState<BlockAnswers>({});
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [corrected, setCorrected] = useState<Record<string, string>>({});
  const [activeToolCallIndex, setActiveToolCallIndex] = useState(0);
  const [approvedToolCalls, setApprovedToolCalls] = useState<Set<string>>(new Set());
  const [diffFilter, setDiffFilter] = useState<"all" | "added" | "removed" | "moved">("all");

  // Mock the upload + scan progression.
  const startUpload = () => {
    setUploadState("uploading");
    setProgress(20);
    setFileName("april-2026-roster-v3.xlsx");
    setTimeout(() => setProgress(60), 350);
    setTimeout(() => {
      setProgress(100);
      setUploadState("uploaded");
      setSurface("scanning");
    }, 800);
    setTimeout(() => setSurface("review"), 1800);
  };

  // Required HITL gating — submit disabled until all required answered.
  // Covers both the roster CLARIFY/VERIFICATION turns and the report clarify turn.
  const allRequiredAnswered = useMemo(() => {
    if (surface === "review") {
      const turn = answers["__viewing__"] === "verification" ? VERIFICATION_TURN : CLARIFY_TURN;
      const required = turn.blocks.filter(
        (b): b is ClarifyingQuestionBlock => b.kind === "clarifying_question" && !!b.required,
      );
      return required.every((q) => (answers[q.question_id] ?? "").trim().length > 0);
    }
    if (surface === "report_clarify") {
      const required = REPORT_CLARIFY_TURN.blocks.filter(
        (b): b is ClarifyingQuestionBlock => b.kind === "clarifying_question" && !!b.required,
      );
      return required.every((q) => (answers[q.question_id] ?? "").trim().length > 0);
    }
    return true;
  }, [answers, surface]);

  const [reportPrompt, setReportPrompt] = useState("");
  const [savedReports, setSavedReports] = useState<SavedReport[]>(MOCK_SAVED_REPORTS);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveTitle, setSaveTitle] = useState("");
  const [saveScope, setSaveScope] = useState<SavedReportScope>("fixed");
  const [lastSavedReport, setLastSavedReport] = useState<SavedReport | null>(null);

  const goToPicker = () => {
    setSurface("agent_picker");
    setReportPrompt("");
    setAnswers({});
  };

  // Suggested defaults for the Save dialog from the current report state.
  // Title derives from the prompt + chosen date range; scope defaults to fixed
  // unless the date range answer is one of the rolling presets (ytd / rolling).
  const dateAnswer = answers["rpt_date_range"] ?? "april_2026";
  const dateScopeDefault: SavedReportScope =
    dateAnswer === "ytd" || dateAnswer === "rolling_30" ? "rolling" : "fixed";
  const dateLabelFor = (scope: SavedReportScope): string => {
    if (scope === "rolling") return dateAnswer === "ytd" ? "Year to date" : "Rolling 30 days";
    if (dateAnswer === "march_april") return "March + April 2026";
    if (dateAnswer === "custom") return "Custom range";
    return "April 2026";
  };

  const openSaveDialog = () => {
    setSaveTitle(
      reportPrompt
        ? reportPrompt.charAt(0).toUpperCase() + reportPrompt.slice(1)
        : "Custom report",
    );
    setSaveScope(dateScopeDefault);
    setSaveDialogOpen(true);
  };

  const confirmSave = () => {
    const newReport: SavedReport = {
      id: `rep-${Date.now()}`,
      title: saveTitle.trim() || "Custom report",
      prompt: reportPrompt,
      savedAt: new Date().toISOString().slice(0, 10),
      scope: saveScope,
      dateLabel: dateLabelFor(saveScope),
      groupBy: answers["rpt_group_by"] === "site_employee" ? "Site → employee" : "Site",
      metrics: (answers["rpt_metrics"] ?? "ot_hours,ot_cost,ot_events,ot_avg_emp")
        .split(",")
        .map((m) => {
          switch (m.trim()) {
            case "ot_hours":   return "OT hours";
            case "ot_cost":    return "OT cost";
            case "ot_events":  return "# OT events";
            case "ot_avg_emp": return "Avg per employee";
            default: return m.trim();
          }
        })
        .filter(Boolean),
    };
    setSavedReports((prev) => [newReport, ...prev]);
    setLastSavedReport(newReport);
    setSaveDialogOpen(false);
    setSurface("report_complete");
  };
  const BackToPicker = (
    <button
      type="button"
      onClick={goToPicker}
      className="inline-flex items-center gap-xxs text-xs text-greyscale-text-caption hover:text-primary-navy-900 focus:outline-none focus:underline"
    >
      <ChevronLeft size={12} />
      <span>Choose a different agent</span>
    </button>
  );

  /* -------- Agent picker (default landing) ------------------------- */
  if (surface === "agent_picker") {
    return (
      <AgentPicker
        onPickRoster={() => setSurface("pre_import")}
        onPickReport={() => {
          setReportPrompt("");
          setAnswers({});
          setSurface("report_compose");
        }}
        savedReports={savedReports}
        onOpenSaved={(r) => {
          // Rerun-fresh: regenerate against current data. In the mock this
          // jumps straight to the preview surface with the saved prompt
          // populated; real impl would re-execute the query.
          setReportPrompt(r.prompt);
          setLastSavedReport(r);
          setSurface("report_preview");
        }}
        onDeleteSaved={(id) =>
          setSavedReports((prev) => prev.filter((r) => r.id !== id))
        }
      />
    );
  }

  /* -------- Report chat: compose (empty prompt state) -------------- */
  if (surface === "report_compose") {
    const submitPrompt = (text: string) => {
      setReportPrompt(text);
      setSurface("report_clarify");
    };
    return (
      <div className="space-y-md">
        {BackToPicker}
        <ReportPhaseRail phase="compose" title="New report" />
        <div className="rounded-lg border border-greyscale-border bg-white p-lg space-y-md">
          <div className="flex items-center gap-sm">
            <Sparkles size={18} className="text-secondary-pink-900" />
            <p className="text-sm font-semibold text-greyscale-text-title">
              What do you want to report on?
            </p>
          </div>
          <p className="text-xs text-greyscale-text-caption">
            Describe the report in plain English. I'll ask a few quick questions about scope and
            metrics, show you a preview, and save it to General Reports once you approve.
          </p>
          <Textarea
            value={reportPrompt}
            onChange={(e) => setReportPrompt(e.target.value)}
            placeholder="e.g. Show me overtime by site for April"
            rows={3}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && reportPrompt.trim()) {
                e.preventDefault();
                submitPrompt(reportPrompt.trim());
              }
            }}
          />
          <div className="flex items-center justify-between gap-sm flex-wrap">
            <p className="text-xs text-greyscale-text-caption">
              <span className="font-medium">Tip:</span> ⌘+Enter to send
            </p>
            <Button
              onClick={() => submitPrompt(reportPrompt.trim())}
              disabled={!reportPrompt.trim()}
              leftIcon={<Send size={14} />}
            >
              Send
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-greyscale-border bg-white p-md">
          <p className="text-xs font-semibold text-greyscale-text-title mb-sm">
            Or start with one of these
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-sm">
            {REPORT_PROMPT_EXAMPLES.map((ex) => (
              <button
                key={ex.id}
                type="button"
                onClick={() => submitPrompt(ex.label)}
                className="text-left rounded-md border border-greyscale-border bg-white p-sm hover:border-primary-navy-900 hover:bg-neutral-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-navy-900 focus:ring-offset-2"
              >
                <div className="flex items-start gap-xs">
                  <MessageSquare
                    size={14}
                    className="mt-[2px] shrink-0 text-greyscale-text-caption"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-greyscale-text-title">{ex.label}</p>
                    <p className="text-xs text-greyscale-text-caption">{ex.hint}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* -------- Report chat: clarify (agent's clarifying turn) --------- */
  if (surface === "report_clarify") {
    return (
      <div className="space-y-md">
        {BackToPicker}
        <ReportPhaseRail phase="clarify" title={reportPrompt || "New report"} />

        {/* The operator's original prompt as a user turn */}
        <div className="rounded-lg border border-greyscale-border bg-white p-md">
          <div className="flex items-start gap-sm">
            <div className="rounded-full bg-primary-navy-50 p-xxs">
              <UsersIcon size={14} className="text-primary-navy-900" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-greyscale-text-caption mb-xxxs">You</p>
              <p className="text-sm text-greyscale-text-title">{reportPrompt}</p>
            </div>
          </div>
        </div>

        {/* Agent response — typed-block renderer */}
        <div className="rounded-lg border border-greyscale-border bg-white p-md space-y-md">
          {REPORT_CLARIFY_TURN.blocks
            .filter((b) => b.kind !== "permission_request")
            .map((b, i) => (
              <BlockRenderer
                key={i}
                block={b}
                answers={answers}
                onAnswerChange={(id, v) => setAnswers((a) => ({ ...a, [id]: v }))}
              />
            ))}
        </div>

        {/* Permission request — gated by required answers */}
        {REPORT_CLARIFY_TURN.blocks
          .filter((b): b is PermissionRequestBlock => b.kind === "permission_request")
          .map((b, i) => (
            <div key={i} className={!allRequiredAnswered ? "opacity-50 pointer-events-none" : ""}>
              <PermissionRequest
                block={b}
                onApprove={() => setSurface("report_preview")}
                onReject={() => setSurface("report_compose")}
              />
            </div>
          ))}

        {!allRequiredAnswered && (
          <Alert variant="note">
            <AlertTitle>Answer the required questions to continue</AlertTitle>
            <AlertDescription>
              Date range, metrics, and group-by are required. Weekends is optional.
            </AlertDescription>
          </Alert>
        )}

        <ReportChatInput
          placeholder="Refine the report or ask a follow-up — e.g. 'also break out by weekday'"
          helper="Follow-ups appear inline. ⌘+Enter to send."
        />
      </div>
    );
  }

  /* -------- Report chat: preview (generated report) ---------------- */
  if (surface === "report_preview") {
    return (
      <div className="space-y-md">
        {BackToPicker}
        <ReportPhaseRail phase="preview" title={reportPrompt || "New report"} />

        <Alert variant="success">
          <AlertTitle>Preview generated</AlertTitle>
          <AlertDescription>
            Based on your prompt and choices. Nothing is saved yet — Save to add this report to
            General Reports, or Refine to keep iterating.
          </AlertDescription>
        </Alert>

        <div className="rounded-lg border border-greyscale-border bg-white p-md">
          <div className="flex items-center gap-sm mb-sm">
            <FileBarChart size={16} className="text-primary-navy-900" />
            <p className="text-sm font-semibold text-greyscale-text-title">
              April 2026 — Overtime by site
            </p>
            <Tag variant="processing" size="sm">Preview</Tag>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-greyscale-border text-left text-xs text-greyscale-text-caption">
                  <th className="py-xs pr-md font-medium">Site</th>
                  <th className="py-xs pr-md font-medium">OT hours</th>
                  <th className="py-xs pr-md font-medium">OT cost</th>
                  <th className="py-xs pr-md font-medium"># OT events</th>
                  <th className="py-xs font-medium">Avg per employee</th>
                </tr>
              </thead>
              <tbody>
                {REPORT_PREVIEW_ROWS.map((r) => (
                  <tr key={r.site} className="border-b border-greyscale-border last:border-0">
                    <td className="py-sm pr-md font-medium text-greyscale-text-title">{r.site}</td>
                    <td className="py-sm pr-md">{r.otHours}</td>
                    <td className="py-sm pr-md">{r.otCost}</td>
                    <td className="py-sm pr-md">{r.otEvents}</td>
                    <td className="py-sm">{r.otAvgEmp}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-greyscale-text-caption mt-sm">
            6 sites, 142 employees, 1 April – 30 April 2026.
          </p>
        </div>

        <div className="flex items-center justify-between gap-sm flex-wrap">
          <Button
            variant="outline"
            onClick={() => setSurface("report_clarify")}
            leftIcon={<Pencil size={14} />}
          >
            Refine
          </Button>
          <div className="flex items-center gap-xs">
            <Button variant="outline" onClick={() => setSurface("report_compose")}>
              Discard
            </Button>
            <Button onClick={openSaveDialog} leftIcon={<Check size={14} />}>
              Save report
            </Button>
          </div>
        </div>

        <ReportChatInput
          placeholder="Refine — e.g. 'add a column for headcount' or 'group by site and shift'"
          helper="Refinements re-run the report. ⌘+Enter to send."
        />

        {/* Save Dialog — title + rolling vs fixed disambiguation.
            Forces the operator to decide at save time whether the date range
            is a query ("rolling 30 days") or a snapshot ("April 2026") so
            rerun semantics are unambiguous later. */}
        <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save this report</DialogTitle>
              <DialogDescription>
                Saved reports rerun against current data. Choose how the date range should behave
                when you rerun later.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-md">
              <div className="space-y-xs">
                <Label htmlFor="save-title">Report name</Label>
                <InputField
                  id="save-title"
                  value={saveTitle}
                  onChange={(e) => setSaveTitle(e.target.value)}
                  placeholder="e.g. Overtime by site — April 2026"
                />
              </div>

              <div className="space-y-xs">
                <Label>Date range behavior</Label>
                <RadioGroup
                  value={saveScope}
                  onValueChange={(v) => setSaveScope(v as SavedReportScope)}
                  className="flex flex-col gap-xs"
                >
                  <Label className="flex items-start gap-sm rounded-md border border-greyscale-border p-sm cursor-pointer">
                    <RadioGroupItem value="fixed" id="scope-fixed" />
                    <span>
                      <span className="text-sm font-semibold block">
                        Pin to {dateLabelFor("fixed")}
                      </span>
                      <span className="text-xs text-greyscale-text-caption block">
                        Always reports on the same absolute dates. Rerun shows current data for
                        that fixed window — useful for month-over-month comparisons.
                      </span>
                    </span>
                  </Label>
                  <Label className="flex items-start gap-sm rounded-md border border-greyscale-border p-sm cursor-pointer">
                    <RadioGroupItem value="rolling" id="scope-rolling" />
                    <span>
                      <span className="text-sm font-semibold block">
                        Rolling — recompute window each rerun
                      </span>
                      <span className="text-xs text-greyscale-text-caption block">
                        Re-resolves the date window on every rerun. "Last 30 days" stays "last 30
                        days" — useful for operational dashboards.
                      </span>
                    </span>
                  </Label>
                </RadioGroup>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={confirmSave}
                disabled={!saveTitle.trim()}
                leftIcon={<Check size={14} />}
              >
                Save report
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  /* -------- Report chat: complete (saved) -------------------------- */
  if (surface === "report_complete") {
    return (
      <div className="space-y-md">
        {BackToPicker}
        <ReportPhaseRail phase="complete" title={reportPrompt || "New report"} />

        <Alert variant="success">
          <AlertTitle>Report saved</AlertTitle>
          <AlertDescription>
            {lastSavedReport
              ? `"${lastSavedReport.title}" is now available under Saved custom reports on this tab. Rerun any time to regenerate against current data.`
              : `Your report is now available under Saved custom reports on this tab. Rerun any time to regenerate against current data.`}
          </AlertDescription>
        </Alert>

        <div className="rounded-lg border border-greyscale-border bg-white p-md flex items-start gap-sm">
          <FileBarChart size={18} className="text-primary-navy-900 mt-xxxs shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-xs flex-wrap mb-xxxs">
              <p className="text-sm font-semibold text-greyscale-text-title">
                {lastSavedReport?.title ?? "Custom report"}
              </p>
              {lastSavedReport && (
                <Tag
                  variant={lastSavedReport.scope === "rolling" ? "processing" : "drafted"}
                  size="sm"
                >
                  {lastSavedReport.dateLabel}
                </Tag>
              )}
            </div>
            <p className="text-xs text-greyscale-text-caption">
              {lastSavedReport
                ? `Grouped by ${lastSavedReport.groupBy} · ${lastSavedReport.metrics.length} metric${
                    lastSavedReport.metrics.length === 1 ? "" : "s"
                  } · ${lastSavedReport.dateLabel}`
                : "6 sites · 4 metrics · grouped by site · April 2026"}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={goToPicker}>
            View saved reports
          </Button>
        </div>

        <div className="flex items-center gap-xs">
          <Button
            onClick={() => {
              setReportPrompt("");
              setAnswers({});
              setSurface("report_compose");
            }}
            leftIcon={<RotateCcw size={14} />}
          >
            Start a new report
          </Button>
          <Button variant="outline" onClick={goToPicker}>
            Back to agents
          </Button>
        </div>
      </div>
    );
  }

  /* -------- Resume card (variant === 'resume') --------------------- */
  if (variant === "resume" && surface === "pre_import") {
    return (
      <div className="space-y-md">
        {BackToPicker}
        <ResumeCard
          onContinue={() => {
            setFileName(RESUME_MOCK.fileName);
            setSurface("review");
            setAnswers({ ...answers, __viewing__: "requesting_context" });
          }}
          onDiscard={() => {
            // Discard returns to a clean pre_import — would call backend in real impl.
          }}
        />
        <PreImportUpload onUpload={startUpload} state={uploadState} progress={progress} fileName={fileName} />
      </div>
    );
  }

  /* -------- Agent unavailable (variant === 'agent_unavailable') ---- */
  if (surface === "agent_unavailable") {
    return (
      <Alert variant="destructive" className="mt-md">
        <AlertTitle>Roster agent unavailable</AlertTitle>
        <AlertDescription>
          <p className="mb-sm">
            The agent didn't respond in time. Your upload wasn't saved. Try again, or contact support
            if this keeps happening.
          </p>
          <div className="flex gap-xs">
            <Button size="sm" onClick={() => setSurface("pre_import")} leftIcon={<RotateCcw size={14} />}>
              Retry
            </Button>
            <Button size="sm" variant="outline">
              Contact support
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  /* -------- Pre-import: upload zone --------------------------------- */
  if (surface === "pre_import") {
    return (
      <div className="space-y-md">
        {BackToPicker}
        <PreImportUpload
          onUpload={startUpload}
          state={uploadState}
          progress={progress}
          fileName={fileName}
        />
      </div>
    );
  }

  /* -------- Scanning (post-upload, pre-block-render) ---------------- */
  if (surface === "scanning") {
    return (
      <div className="rounded-lg border border-greyscale-border bg-white p-lg space-y-md">
        <div className="flex items-center gap-sm">
          <Bot size={18} className="text-primary-navy-900" />
          <p className="text-sm font-semibold text-greyscale-text-title">
            Scanning {fileName ?? "your file"}…
          </p>
        </div>
        <Progress value={75} />
        <p className="text-xs text-greyscale-text-caption">
          Reading the file, finding employees and sites. This takes 5-15 seconds for a typical roster.
        </p>
        <div className="space-y-xs">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    );
  }

  /* -------- Review (HITL conversation) ------------------------------ */
  if (surface === "review") {
    const viewingPhase = answers.__viewing__ ?? "initial_scan";
    const currentTurn =
      viewingPhase === "verification"
        ? VERIFICATION_TURN
        : viewingPhase === "requesting_context"
          ? CLARIFY_TURN
          : INITIAL_SCAN_TURN;
    const showSideBySide = viewingPhase === "verification";

    return (
      <div className="space-y-md">
        <PhaseRail phase={currentTurn.phase} fileName={fileName ?? RESUME_MOCK.fileName} />

        {/* Block stream */}
        <div className="space-y-sm">
          {/* Stat blocks — gather them into a grid */}
          {currentTurn.blocks.some((b) => b.kind === "stat") && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-sm">
              {currentTurn.blocks
                .filter((b): b is StatBlock => b.kind === "stat")
                .map((b, i) => (
                  <StatTile key={i} stat={b} />
                ))}
            </div>
          )}
          {currentTurn.blocks
            .filter((b) => b.kind !== "stat")
            .map((b, i) => {
              if (b.kind === "permission_request") {
                return (
                  <PermissionRequest
                    key={i}
                    block={b}
                    onApprove={() => {
                      // Move into the tool-call interrupt stream.
                      setActiveToolCallIndex(0);
                      setSurface("tool_call");
                    }}
                    onReject={() => {
                      setAnswers({ ...answers, __viewing__: "requesting_context" });
                    }}
                  />
                );
              }
              return (
                <BlockRenderer
                  key={i}
                  block={b}
                  answers={answers}
                  onAnswerChange={(id, v) => setAnswers((a) => ({ ...a, [id]: v }))}
                />
              );
            })}
        </div>

        {/* Side-by-side review surfaces only in verification phase */}
        {showSideBySide && (
          <div className="rounded-lg border border-greyscale-border bg-white p-md">
            <div className="flex items-start justify-between gap-sm mb-sm flex-wrap">
              <div>
                <h4 className="text-sm font-semibold text-greyscale-text-title">
                  Side-by-side review
                </h4>
                <p className="text-xs text-greyscale-text-caption mt-xxxs">
                  Each source row paired with what Jem will create. Skip or correct any row before
                  you approve.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-xs">
                <Tag variant="success" size="sm">
                  {REVIEW_ROWS.length - skipped.size} included
                </Tag>
                <Tag variant="drafted" size="sm">{skipped.size} skipped</Tag>
              </div>
            </div>
            <div className="space-y-xs">
              {REVIEW_ROWS.map((row) => (
                <ReviewRowItem
                  key={row.id}
                  row={row}
                  skipped={skipped.has(row.id)}
                  onToggleSkip={() => {
                    setSkipped((s) => {
                      const next = new Set(s);
                      if (next.has(row.id)) next.delete(row.id);
                      else next.add(row.id);
                      return next;
                    });
                  }}
                  correctedTo={corrected[row.id]}
                  onCorrect={(emp) => {
                    if (emp === "__skip__") {
                      setSkipped((s) => new Set(s).add(row.id));
                    } else {
                      setCorrected((c) => ({ ...c, [row.id]: emp }));
                      setSkipped((s) => {
                        if (!s.has(row.id)) return s;
                        const next = new Set(s);
                        next.delete(row.id);
                        return next;
                      });
                    }
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* HITL forward-progress bar */}
        {viewingPhase === "requesting_context" && (
          <div className="sticky bottom-0 -mx-md sm:mx-0 bg-white border-t border-greyscale-border p-md flex items-center justify-between gap-sm rounded-b-lg">
            <p className="text-xs text-greyscale-text-caption">
              {allRequiredAnswered
                ? "All required questions answered."
                : "Answer the required questions marked with * to continue."}
            </p>
            <Button
              disabled={!allRequiredAnswered}
              onClick={() => setAnswers((a) => ({ ...a, __viewing__: "verification" }))}
              rightIcon={<ArrowRight size={14} />}
            >
              Continue to review
            </Button>
          </div>
        )}
        {viewingPhase === "initial_scan" && (
          <div className="flex justify-end">
            <Button
              onClick={() => setAnswers((a) => ({ ...a, __viewing__: "requesting_context" }))}
              rightIcon={<ArrowRight size={14} />}
            >
              Answer 4 clarifying questions
            </Button>
          </div>
        )}
      </div>
    );
  }

  /* -------- Tool-call approval Dialog ------------------------------- */
  if (surface === "tool_call") {
    const call = TOOL_CALLS[activeToolCallIndex];
    return (
      <div className="space-y-md">
        <PhaseRail phase="saving" fileName={fileName ?? RESUME_MOCK.fileName} />
        <div className="rounded-lg border border-greyscale-border bg-white p-md">
          <h4 className="text-sm font-semibold text-greyscale-text-title mb-sm">
            Database writes ({approvedToolCalls.size}/{TOOL_CALLS.length} approved)
          </h4>
          <div className="space-y-xs">
            {TOOL_CALLS.map((tc, i) => (
              <div
                key={tc.id}
                className={cn(
                  "rounded-md border p-sm flex items-center gap-sm",
                  approvedToolCalls.has(tc.id)
                    ? "border-green-300 bg-green-50"
                    : i === activeToolCallIndex
                      ? "border-secondary-pink-300 bg-secondary-pink-50"
                      : "border-greyscale-border bg-white",
                )}
              >
                {approvedToolCalls.has(tc.id) ? (
                  <CheckCircle2 size={16} className="text-green-700" />
                ) : i === activeToolCallIndex ? (
                  <ShieldAlert size={16} className="text-secondary-pink-900" />
                ) : (
                  <Calendar size={16} className="text-greyscale-text-caption" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-greyscale-text-title">
                    {TOOL_HUMAN_NAME[tc.tool]}
                  </p>
                  <p className="text-xs text-greyscale-text-caption">{tc.description}</p>
                </div>
                {approvedToolCalls.has(tc.id) && (
                  <Tag variant="success" size="sm">Approved</Tag>
                )}
              </div>
            ))}
          </div>
        </div>
        <ToolCallDialog
          call={call}
          onApprove={() => {
            const next = new Set(approvedToolCalls).add(call.id);
            setApprovedToolCalls(next);
            if (activeToolCallIndex + 1 < TOOL_CALLS.length) {
              setActiveToolCallIndex(activeToolCallIndex + 1);
            } else {
              setSurface("saving");
              setTimeout(() => setSurface("complete"), 1200);
            }
          }}
          onReject={() => {
            setSurface("review");
            setAnswers((a) => ({ ...a, __viewing__: "verification" }));
          }}
          onClose={() => {
            setSurface("review");
            setAnswers((a) => ({ ...a, __viewing__: "verification" }));
          }}
        />
      </div>
    );
  }

  /* -------- Saving (between last tool-call and complete) ------------ */
  if (surface === "saving") {
    return (
      <div className="rounded-lg border border-greyscale-border bg-white p-lg space-y-md">
        <div className="flex items-center gap-sm">
          <Bot size={18} className="text-primary-navy-900" />
          <p className="text-sm font-semibold text-greyscale-text-title">
            Writing assignments to Jem…
          </p>
        </div>
        <Progress value={85} />
        <p className="text-xs text-greyscale-text-caption">
          All database writes approved. Saving 72 assignments. This usually completes in under 10 seconds.
        </p>
      </div>
    );
  }

  /* -------- Complete (post-import) ---------------------------------- */
  if (surface === "complete") {
    return (
      <div className="space-y-md">
        <PhaseRail phase="extraction_complete" fileName={fileName ?? RESUME_MOCK.fileName} />
        <Alert variant="success">
          <AlertTitle>Roster imported</AlertTitle>
          <AlertDescription>
            April 2026 roster is live. 58 new assignments, 14 replaced, 3 skipped, 1 new location.
          </AlertDescription>
        </Alert>

        {/* End-state stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-sm">
          {COMPLETE_TURN.blocks
            .filter((b): b is StatBlock => b.kind === "stat")
            .map((b, i) => (
              <StatTile key={i} stat={b} />
            ))}
        </div>

        <Tabs defaultValue="assignments">
          <TabsList variant="line" className="w-full justify-start">
            <TabsTrigger value="assignments" variant="line">
              Assignments ({POST_ASSIGNMENTS.length})
            </TabsTrigger>
            <TabsTrigger value="unmatched" variant="line">
              Unmatched employees ({UNMATCHED.length})
            </TabsTrigger>
            <TabsTrigger value="diff" variant="line">
              Diff vs March 2026
            </TabsTrigger>
          </TabsList>
          <TabsContent value="assignments">
            <PostImportAssignments />
          </TabsContent>
          <TabsContent value="unmatched">
            <UnmatchedList />
          </TabsContent>
          <TabsContent value="diff">
            {variant === "first_ever_employer" ? (
              <div className="rounded-lg border border-greyscale-border bg-neutral-50 p-lg text-center">
                <History size={28} className="text-greyscale-text-caption mx-auto mb-xs" />
                <p className="text-sm font-semibold text-greyscale-text-title">
                  First roster for this employer — nothing to compare.
                </p>
                <p className="text-xs text-greyscale-text-caption mt-xxs">
                  Future imports will show what changed since this one.
                </p>
              </div>
            ) : (
              <DiffView filter={diffFilter} onFilterChange={setDiffFilter} />
            )}
          </TabsContent>
        </Tabs>

        <div>
          <Button
            variant="outline"
            leftIcon={<RotateCcw size={14} />}
            onClick={() => {
              setSurface("pre_import");
              setUploadState("idle");
              setProgress(0);
              setFileName(undefined);
              setAnswers({});
              setSkipped(new Set());
              setCorrected({});
              setApprovedToolCalls(new Set());
              setActiveToolCallIndex(0);
            }}
          >
            Start a new roster import
          </Button>
        </div>
      </div>
    );
  }

  return null;
}

/* ====================================================================== */
/*  Sub-components                                                        */
/* ====================================================================== */

function PreImportUpload({
  onUpload,
  state,
  progress,
  fileName,
}: {
  onUpload: () => void;
  state: UploadState;
  progress: number;
  fileName?: string;
}) {
  return (
    <div className="space-y-md">
      <div className="rounded-lg border border-greyscale-border bg-white p-lg">
        <div className="flex items-start gap-sm mb-md">
          <Bot size={20} className="text-primary-navy-900 mt-xxxs shrink-0" />
          <div>
            <h3 className="text-base font-semibold text-greyscale-text-title">
              Import a roster from Excel, CSV, or PDF
            </h3>
            <p className="text-sm text-greyscale-text-body mt-xxxs">
              I'll read the file, ask you to confirm anything ambiguous, and write the assignments
              to Jem only after you approve each one. Nothing is written without your sign-off.
            </p>
          </div>
        </div>
        <Upload
          state={
            state === "uploading"
              ? "uploading"
              : state === "uploaded"
                ? "uploaded"
                : "default"
          }
          progress={progress}
          fileName={fileName}
          title="Upload a roster file"
          description="Accepted: .xlsx, .csv, .pdf"
          maxSize="20 MB"
          onSelectFile={onUpload}
          onSubmit={onUpload}
        />
      </div>

      <div className="rounded-lg border border-greyscale-border bg-neutral-50 p-md">
        <p className="text-xs font-semibold uppercase tracking-wide text-greyscale-text-caption mb-xs">
          What happens after I upload?
        </p>
        <ol className="space-y-xs text-sm text-greyscale-text-body list-decimal pl-md">
          <li>Jem scans the file and pulls out employees, sites, and shifts.</li>
          <li>You answer any clarifying questions Jem can't decide on its own.</li>
          <li>You see a side-by-side preview of every row before anything is written.</li>
          <li>You approve each database write individually.</li>
        </ol>
      </div>
    </div>
  );
}

function ResumeCard({
  onContinue,
  onDiscard,
}: {
  onContinue: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="rounded-lg border-2 border-primary-navy-200 bg-primary-navy-50 p-md">
      <div className="flex items-start gap-sm">
        <History size={18} className="text-primary-navy-900 mt-xxxs shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-greyscale-text-title">
            You have a roster in review
          </p>
          <p className="text-xs text-greyscale-text-body mt-xxxs">
            <span className="font-semibold">{RESUME_MOCK.fileName}</span> · uploaded{" "}
            {RESUME_MOCK.uploadedAt} · {RESUME_MOCK.questionsLeft} clarifying questions still need answers
          </p>
        </div>
        <div className="flex items-center gap-xs">
          <Button size="sm" variant="outline" onClick={onDiscard}>
            Discard
          </Button>
          <Button size="sm" onClick={onContinue} rightIcon={<ArrowRight size={14} />}>
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}

function PhaseRail({ phase, fileName }: { phase: Phase; fileName: string }) {
  const phaseLabel: Record<Phase, string> = {
    conversation: "Conversation",
    context_gathering: "Gathering context",
    initial_scan: "1. File scanned",
    requesting_context: "2. Clarifying questions",
    matching: "3. Matching",
    verification: "3. Side-by-side review",
    saving: "4. Approving writes",
    extraction_complete: "5. Import complete",
    error: "Error",
  };
  return (
    <div className="flex items-center justify-between gap-sm rounded-md bg-white border border-greyscale-border px-md py-sm">
      <div className="flex items-center gap-xs min-w-0">
        <FileSpreadsheet size={14} className="text-greyscale-text-caption shrink-0" />
        <p className="text-xs text-greyscale-text-caption truncate">
          <span className="font-semibold text-greyscale-text-title">{fileName}</span>
        </p>
      </div>
      <Tag variant="processing" size="sm">
        {phaseLabel[phase]}
      </Tag>
    </div>
  );
}

function PostImportAssignments() {
  const columns: ColumnDef<AssignmentRow>[] = [
    {
      accessorKey: "employee",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Employee" />,
      cell: ({ row }) => (
        <div>
          <p className="text-sm font-semibold text-greyscale-text-title">{row.original.employee}</p>
          <p className="text-xs text-greyscale-text-caption">{row.original.empNumber}</p>
        </div>
      ),
    },
    {
      accessorKey: "site",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Site" />,
    },
    {
      accessorKey: "weekday",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Mon-Fri" />,
    },
    {
      accessorKey: "saturday",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Sat" />,
    },
    {
      accessorKey: "sunday",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Sun" />,
    },
  ];
  return (
    <div className="rounded-lg border border-greyscale-border bg-white p-md">
      <p className="text-xs text-greyscale-text-caption mb-sm">
        Every employee in the imported roster with their new site and shift cadence.
      </p>
      <DataTable columns={columns} data={POST_ASSIGNMENTS} />
    </div>
  );
}

function UnmatchedList() {
  const [actionTarget, setActionTarget] = useState<UnmatchedRow | null>(null);
  return (
    <div className="space-y-sm">
      <Alert variant="note">
        <AlertTitle>Unmatched employees are flagged, not deleted</AlertTitle>
        <AlertDescription>
          Two roster rows reference names that don't exist in Jem. They aren't removed from your
          system — pick a follow-up action below.
        </AlertDescription>
      </Alert>
      {UNMATCHED.map((u) => (
        <div
          key={u.id}
          className="rounded-lg border border-greyscale-border bg-white p-md flex items-start gap-sm"
        >
          <UserMinus size={16} className="text-orange-600 mt-xxs shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-greyscale-text-title">{u.name}</p>
            <p className="text-xs text-greyscale-text-caption mt-xxxs">{u.source}</p>
            <p className="text-xs text-greyscale-text-body mt-xxxs">{u.reason}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setActionTarget(u)}>
            Take action
          </Button>
        </div>
      ))}
      <Dialog open={!!actionTarget} onOpenChange={(o) => !o && setActionTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Follow up on {actionTarget?.name}</DialogTitle>
            <DialogDescription>
              Pick what to do with this unmatched roster row. The employee is never deleted from Jem
              as a side-effect of this choice.
            </DialogDescription>
          </DialogHeader>
          <RadioGroup defaultValue="invite" className="flex flex-col gap-xs">
            <Label className="flex items-start gap-sm rounded-md border border-greyscale-border p-sm cursor-pointer">
              <RadioGroupItem value="invite" id="action-invite" />
              <span>
                <span className="block text-sm font-semibold">Invite as a new hire</span>
                <span className="block text-xs text-greyscale-text-caption">
                  Send an onboarding invite — appears in Employees as Pending.
                </span>
              </span>
            </Label>
            <Label className="flex items-start gap-sm rounded-md border border-greyscale-border p-sm cursor-pointer">
              <RadioGroupItem value="casual" id="action-casual" />
              <span>
                <span className="block text-sm font-semibold">Mark as casual / contractor</span>
                <span className="block text-xs text-greyscale-text-caption">
                  Create a casual record — no Hub access, time tracking only.
                </span>
              </span>
            </Label>
            <Label className="flex items-start gap-sm rounded-md border border-greyscale-border p-sm cursor-pointer">
              <RadioGroupItem value="relink" id="action-relink" />
              <span>
                <span className="block text-sm font-semibold">Re-link to an existing employee</span>
                <span className="block text-xs text-greyscale-text-caption">
                  Probably a typo — pick the right employee.
                </span>
              </span>
            </Label>
            <Label className="flex items-start gap-sm rounded-md border border-greyscale-border p-sm cursor-pointer">
              <RadioGroupItem value="dismiss" id="action-dismiss" />
              <span>
                <span className="block text-sm font-semibold">Dismiss</span>
                <span className="block text-xs text-greyscale-text-caption">
                  Ignore this row — not on staff. Doesn't affect any existing employee.
                </span>
              </span>
            </Label>
          </RadioGroup>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionTarget(null)}>
              Cancel
            </Button>
            <Button onClick={() => setActionTarget(null)}>Save action</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DiffView({
  filter,
  onFilterChange,
}: {
  filter: "all" | "added" | "removed" | "moved";
  onFilterChange: (f: "all" | "added" | "removed" | "moved") => void;
}) {
  const buckets = {
    added: DIFF_ROWS.filter((r) => r.bucket === "added"),
    removed: DIFF_ROWS.filter((r) => r.bucket === "removed"),
    moved: DIFF_ROWS.filter((r) => r.bucket === "moved"),
  };
  const rows = filter === "all" ? DIFF_ROWS : DIFF_ROWS.filter((r) => r.bucket === filter);

  const columns: ColumnDef<DiffRow>[] = [
    {
      accessorKey: "bucket",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Change" />,
      cell: ({ row }) => {
        const b = row.original.bucket;
        if (b === "added") {
          return (
            <Tag variant="success" size="sm">
              <UserPlus size={10} /> Added
            </Tag>
          );
        }
        if (b === "removed") {
          return (
            <Tag variant="failed" size="sm">
              <UserMinus size={10} /> Removed
            </Tag>
          );
        }
        return (
          <Tag variant="drafted" size="sm">
            <ArrowLeftRight size={10} /> Moved
          </Tag>
        );
      },
    },
    {
      accessorKey: "employee",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Employee" />,
    },
    {
      accessorKey: "before",
      header: ({ column }) => <DataTableColumnHeader column={column} title="Before (March)" />,
    },
    {
      accessorKey: "after",
      header: ({ column }) => <DataTableColumnHeader column={column} title="After (April)" />,
    },
  ];
  return (
    <div className="space-y-sm">
      <div className="grid grid-cols-3 gap-sm">
        <div className="rounded-md border border-greyscale-border bg-white p-sm">
          <p className="text-xs text-greyscale-text-caption">Added</p>
          <p className="text-lg font-semibold text-greyscale-text-title">{buckets.added.length}</p>
        </div>
        <div className="rounded-md border border-greyscale-border bg-white p-sm">
          <p className="text-xs text-greyscale-text-caption">Removed</p>
          <p className="text-lg font-semibold text-greyscale-text-title">{buckets.removed.length}</p>
        </div>
        <div className="rounded-md border border-greyscale-border bg-white p-sm">
          <p className="text-xs text-greyscale-text-caption">Moved</p>
          <p className="text-lg font-semibold text-greyscale-text-title">{buckets.moved.length}</p>
        </div>
      </div>
      <div className="rounded-lg border border-greyscale-border bg-white p-md">
        <div className="flex items-center justify-between gap-sm mb-sm flex-wrap">
          <p className="text-xs text-greyscale-text-caption">
            Compared to last roster (March 2026, imported 2026-03-29).
          </p>
          <div className="min-w-[180px]">
            <Select value={filter} onValueChange={(v) => onFilterChange(v as typeof filter)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Show all changes</SelectItem>
                <SelectItem value="added">Added only</SelectItem>
                <SelectItem value="removed">Removed only</SelectItem>
                <SelectItem value="moved">Moved only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DataTable columns={columns} data={rows} />
      </div>
    </div>
  );
}
